import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CategoryIcon from '@material-ui/icons/Category';
import { CustomSidebarExpandButton } from './SidebarExpandButton';
import { GuestUserSettingsRow } from './GuestUserSettingsRow';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { GlobalBreadcrumb } from '../common/GlobalBreadcrumb';
import { TechDocsPageHeader } from '../docs/TechDocsPageHeader';
import { GlobalModals } from '../GlobalModals';
import { MermaidStyleFix } from '../docs/MermaidStyleFix';
import {
  UserSettingsSignInAvatar,
  useUserProfile
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  StarIcon,
} from '@backstage/core-components';
import { SignalsDisplay } from '@backstage/plugin-signals';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
// import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { Grid, Text } from '@backstage/ui';

const useSidebarStyles = makeStyles({
  logo: {
    width: '100%',
    height: '80px',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '8px auto'
  },
  link: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--bui-white)',
  },
  sidebarCollapseBtn: {
    padding: '0px !important',
    minWidth: '30px',
  },
  sidebarNavItemUser: {
    color: 'var(--bui-white)'
  },
  expandButtonWrapper: {
    display: 'flex !important',
    '& > *': {
      display: 'flex !important',
      visibility: 'visible !important',
      opacity: '1 !important',
    },
    '& button': {
      display: 'flex !important',
      visibility: 'visible !important',
      opacity: '1 !important',
    },
    // Override media query hiding
    '@media (max-width: 960px)': {
      display: 'flex !important',
      '& > *': {
        display: 'flex !important',
      },
      '& button': {
        display: 'flex !important',
      },
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.logo}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const UserSettingsRow = () => {
  const classes = useSidebarStyles();
  const { isOpen } = useSidebarOpenState();
  const userProfile = useUserProfile();


  return (
      <Link to='/settings' underline='none'>
        {isOpen ?
          <Grid.Root columns="3" gap="2" style={{width: '100%', alignItems: 'center',}}>
              <Grid.Item colSpan="1" style={{alignSelf: 'center', justifyContent: 'center'}}>
                <UserSettingsSignInAvatar size={36} />
              </Grid.Item>
              <Grid.Item colSpan="2">
                <Text className={classes.sidebarNavItemUser}>{userProfile.displayName}</Text>
              </Grid.Item>
          </Grid.Root>
          :
          <Grid.Root columns="2" gap="2" style={{width: '100%', alignItems: 'center'}}>
            <Grid.Item>
              <UserSettingsSignInAvatar size={36} />
            </Grid.Item>
          </Grid.Root>
        }
      </Link>
  )
}

const UserSettings = () => {
  const guestMode = useApi(configApiRef).getOptionalBoolean('app.localDev.guestMode');
  return guestMode ? <GuestUserSettingsRow /> : <UserSettingsRow />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useSidebarStyles();

  return (
    <div className="rootWrapper">
      <AlertDisplay />
      <OAuthRequestDialog />
      <SignalsDisplay />
      <MermaidStyleFix />
      <SidebarPage>
      <Sidebar disableExpandOnHover submenuOptions={{ drawerWidthOpen: 400 }}>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
          <MyGroupsSidebarItem
            singularTitle="My Group"
            pluralTitle="My Groups"
            icon={GroupIcon}
          />
          <SidebarItem icon={StarIcon} to="product-teams" text="Product Teams" />
          {/* <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" /> */}
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          {/* <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." /> */}
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        {/* <NotificationsSidebarItem /> */}
        <SidebarDivider />
        
        <Grid.Root columns="4"  gap='2' style={{padding: '6px 12px 6px 16px', alignItems: 'center'}}>
          <Grid.Item colSpan="3">
            <UserSettings />
          </Grid.Item>
          <Grid.Item colSpan="1" style={{display: 'flex !important' as any}} className={classes.expandButtonWrapper}>
            <CustomSidebarExpandButton />
          </Grid.Item>
        </Grid.Root>
      </Sidebar>
      <GlobalBreadcrumb />
      <TechDocsPageHeader />
      {children}
      </SidebarPage>
      <GlobalModals />
    </div>
  );
};
